import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from '@apollo/client';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import toast from 'react-hot-toast';
import {
  GET_TOKEN_GROUPS,
} from '../graphql/queries/tokenGroups';
import {
  SET_TOKEN_GROUP,
  CREATE_TOKEN_GROUP,
  RENAME_TOKEN_GROUP,
  DELETE_TOKEN_GROUP,
} from '../graphql/mutations/tokenGroups';

const TYPE_CONFIG = {
  group: {
    title: 'Assign Group',
    itemLabel: 'group',
    createButton: 'New group',
    placeholder: 'Filter groups...',
    listQuery: GET_TOKEN_GROUPS,
    createMutation: CREATE_TOKEN_GROUP,
    renameMutation: RENAME_TOKEN_GROUP,
    deleteMutation: DELETE_TOKEN_GROUP,
    setMutation: SET_TOKEN_GROUP,
    cacheTypename: 'TokenGroup',
    cacheListField: 'tokenGroups',
    removeLabel: 'Remove group',
  },
};

const TokenAssignmentModal = ({
  isOpen,
  onClose,
  tokenId,
  tokenTicker,
  currentItem,
  onApplied,
}) => {
  const cfg = TYPE_CONFIG.group;
  const { currentPortfolioId } = usePortfolioContext();

  const [filter, setFilter] = useState('');
  // merged create into main flow via primary button
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const itemRefs = useRef([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const lastDeletedIdRef = useRef(null);
  const lastDeletedNameRef = useRef('');

  const { data, loading, error, refetch } = useQuery(cfg.listQuery, {
    variables: { portfolioId: currentPortfolioId },
    skip: !currentPortfolioId || !isOpen,
    fetchPolicy: 'cache-and-network',
  });

  const [setAssignment, { loading: assigning }] = useMutation(cfg.setMutation, {
    optimisticResponse: () => true,
    update: (cache, { variables: vars }) => {
      try {
        const list = cache.readQuery({ query: cfg.listQuery, variables: { portfolioId: currentPortfolioId } })?.[cfg.cacheListField] || [];
        const selected = list.find(i => i.id === vars.groupId) || null;
        cache.modify({
          id: cache.identify({ __typename: 'Token', id: tokenId }),
          fields: {
            group(existing) {
              return selected ? { __typename: cfg.cacheTypename, ...selected } : null;
            }
          }
        });
      } catch (e) {
        // ignore cache issues
      }
    },
    onCompleted: () => {
      if (typeof onApplied === 'function') onApplied();
      onClose?.();
    }
  });

  const [createItem, { loading: creating }] = useMutation(cfg.createMutation, {
    onCompleted: async (res) => {
      const newId = res?.createTokenGroup || res?.createTokenLocation;
      if (newId) {
        await setAssignment({ variables: { portfolioId: currentPortfolioId, tokenId, groupId: newId } });
        const createdName = filter.trim();
        toast.success(`${tokenTicker || 'Token'} assigned to ${cfg.itemLabel} ${createdName}`);
        refetch();
      }
    },
    onError: () => {
      toast.error('Could not create. The name may already exist.');
    }
  });

  const [renameItem, { loading: renaming }] = useMutation(cfg.renameMutation, {
    onCompleted: () => {
      setEditingId(null);
      setEditingName('');
      refetch();
    },
    onError: () => {
      toast.error('Could not rename. The name may already exist.');
      // keep editing mode so user can adjust the name
    }
  });

  const [deleteItem, { loading: deleting }] = useMutation(cfg.deleteMutation, {
    onCompleted: async () => {
      const deletedId = lastDeletedIdRef.current;
      const deletedName = lastDeletedNameRef.current;
      setConfirmDeleteId(null);
      refetch();
      // If the current token was assigned to the deleted item, clear assignment
      if (deletedId && currentItem && currentItem.id === deletedId) {
        await setAssignment({ variables: { portfolioId: currentPortfolioId, tokenId, groupId: null } });
      }
      toast.success(`${cfg.itemLabel.charAt(0).toUpperCase() + cfg.itemLabel.slice(1)}${deletedName ? ` "${deletedName}"` : ''} deleted`);
      lastDeletedIdRef.current = null;
      lastDeletedNameRef.current = '';
    },
    onError: () => {
      toast.error('Failed to delete');
    }
  });

  useEffect(() => {
    if (!isOpen) {
      setFilter('');
      setEditingId(null);
      setEditingName('');
      setConfirmDeleteId(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Stable assign helper declared before effects that depend on it
  const quicklyApply = useCallback(async (item) => {
    if (!item) return;
    await setAssignment({ variables: { portfolioId: currentPortfolioId, tokenId, groupId: item.id } });
    toast.success(`${tokenTicker || 'Token'} assigned to ${cfg.itemLabel} ${item.name}`);
  }, [setAssignment, currentPortfolioId, tokenId, cfg.itemLabel, tokenTicker]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.();
      // derive filtered list locally to avoid referencing undeclared vars
      const raw = data?.[cfg.cacheListField] || [];
      const itemsLocal = [...raw].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      const f = filter.trim().toLowerCase();
      const filteredLocal = f ? itemsLocal.filter(it => (it.name || '').toLowerCase().includes(f)) : itemsLocal;

      if (e.key === 'Enter' && document.activeElement === inputRef.current) {
        if (!editingId && !confirmDeleteId) {
          if (filteredLocal.length > 0) {
            const target = filteredLocal[selectedIndex] || filteredLocal[0];
            quicklyApply(target);
          } else if (f.length > 0) {
            createItem({ variables: { portfolioId: currentPortfolioId, name: filter.trim() } });
          }
        }
      }
      if ((e.key === 'ArrowDown' || e.key === 'Down') && !editingId && !confirmDeleteId) {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, Math.max(0, filteredLocal.length - 1)));
      }
      if ((e.key === 'ArrowUp' || e.key === 'Up') && !editingId && !confirmDeleteId) {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose, data, filter, editingId, confirmDeleteId, selectedIndex, cfg.cacheListField, createItem, currentPortfolioId, quicklyApply]);

  // Reset selection when filter changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [filter]);

  // (moved below filteredItems definition)

  const items = useMemo(() => {
    const raw = data?.[cfg.cacheListField] || [];
    return [...raw].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [data, cfg.cacheListField]);

  const filteredItems = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return items;
    return items.filter(it => (it.name || '').toLowerCase().includes(f));
  }, [items, filter]);

  // Default selection to currently assigned item when opening or when list changes
  useEffect(() => {
    if (!isOpen) return;
    let idx = 0;
    if (currentItem && Array.isArray(filteredItems) && filteredItems.length > 0) {
      const found = filteredItems.findIndex(it => it.id === currentItem.id);
      idx = found >= 0 ? found : 0;
    }
    setSelectedIndex(idx);
  }, [isOpen, currentItem, filteredItems]);

  // Ensure selected item stays visible (placed after filteredItems is declared)
  useEffect(() => {
    const el = itemRefs.current[selectedIndex];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, filteredItems]);

  // creation handled by primary button/enter

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditingName(item.name || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const submitEdit = async (id) => {
    const name = editingName.trim();
    if (!name) return;
    await renameItem({ variables: { portfolioId: currentPortfolioId, groupId: id, name } });
  };

  const requestDelete = (id) => setConfirmDeleteId(id);
  const cancelDelete = () => setConfirmDeleteId(null);
  const submitDelete = async (id) => {
    lastDeletedIdRef.current = id;
    try {
      const del = (items || []).find(i => i.id === id) || (filteredItems || []).find(i => i.id === id);
      lastDeletedNameRef.current = del?.name || '';
    } catch (_) {
      lastDeletedNameRef.current = '';
    }
    await deleteItem({ variables: { portfolioId: currentPortfolioId, groupId: id } });
  };

  const removeAssignment = async () => {
    await setAssignment({ variables: { portfolioId: currentPortfolioId, tokenId, groupId: null } });
    const removedName = currentItem?.name || '';
    toast.success(`${tokenTicker || 'Token'} removed from ${cfg.itemLabel} ${removedName}`);
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[85vh] bg-white dark:bg-gray-800 rounded-lg shadow-xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{tokenTicker ? `Assign ${tokenTicker} ${cfg.itemLabel}` : `Assign ${cfg.itemLabel}`}</h3>
          <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" aria-label="Close">
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-4 flex-1 min-h-0 flex flex-col">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              placeholder={cfg.placeholder}
              className="flex-1 p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <button
              onClick={async () => { await (async () => {
                const f = filter.trim();
                const lower = f.toLowerCase();
                if (filteredItems.length > 0) {
                  const target = filteredItems[selectedIndex] || filteredItems[0];
                  if (target) await quicklyApply(target);
                } else if (lower.length > 0) {
                  await createItem({ variables: { portfolioId: currentPortfolioId, name: f } });
                }
              })(); }}
              disabled={assigning || creating || (filter.trim() === '' && (!filteredItems || filteredItems.length === 0))}
              className={`px-4 py-2 rounded-md text-white ${assigning || creating ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              {(() => {
                const f = filter.trim();
                const name = (filteredItems[selectedIndex] || filteredItems[0])?.name;
                if (name) return `Assign to ${name}`;
                if (f.length > 0) return creating ? 'Creating...' : 'Create';
                return 'Assign';
              })()}
            </button>
          </div>

          <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between">
            <div>
              {currentItem ? (
                <>
                  {`Current ${cfg.itemLabel}: ${currentItem.name} `}
                  <button
                    type="button"
                    onClick={removeAssignment}
                    disabled={assigning}
                    className="ml-2 underline text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 disabled:text-gray-400"
                  >
                    remove
                  </button>
                </>
              ) : (
                `No ${cfg.itemLabel} assigned`
              )}
            </div>
            <div>
              {(() => {
                const f = filter.trim();
                const lower = f.toLowerCase();
                const hasExact = lower.length > 0 && filteredItems.some(it => (it.name || '').toLowerCase() === lower);
                if (f && filteredItems.length > 0 && !hasExact) {
                  return (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      or
                      <button
                        type="button"
                        disabled={creating}
                        onClick={async () => { await createItem({ variables: { portfolioId: currentPortfolioId, name: f } }); }}
                        className="ml-1 underline text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 disabled:text-gray-400"
                      >
                        Create "{f}"
                      </button>
                    </span>
                  );
                }
                return null;
              })()}
            </div>
          </div>

          {loading && (
            <div className="animate-pulse h-10 bg-gray-100 dark:bg-gray-700 rounded" />
          )}
          {error && (
            <div className="text-sm text-red-600">Error loading {cfg.itemLabel}s</div>
          )}

          <div ref={listRef} className="divide-y divide-gray-200 dark:divide-gray-700 rounded-md flex-1 min-h-0 overflow-y-auto">
            {filteredItems.map((item, index) => {
              const isEditing = editingId === item.id;
              const isConfirmingDelete = confirmDeleteId === item.id;
              const isCurrent = currentItem?.id === item.id;
              const isSelected = index === selectedIndex && !isEditing && !isConfirmingDelete;
              return (
                <div
                  key={item.id}
                  ref={(el) => { itemRefs.current[index] = el; }}
                  className={`flex items-stretch text-base ${isSelected ? 'bg-blue-100 dark:bg-blue-900/40' : (isCurrent ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-gray-800')}`}
                >
                  {!isEditing && !isConfirmingDelete && (
                    <button
                      onClick={() => quicklyApply(item)}
                      className={`flex-1 text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition ${isCurrent ? 'text-blue-700 dark:text-blue-300' : 'text-gray-800 dark:text-gray-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium truncate">{item.name}</div>
                          {item.description && (<div className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.description}</div>)}
                        </div>
                        {isCurrent && (
                          <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                        )}
                      </div>
                    </button>
                  )}

                  {isEditing && (
                    <div className="flex-1 px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          className="flex-1 p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && !renaming && editingName.trim() && submitEdit(item.id)}
                          autoFocus
                          disabled={renaming}
                        />
                        <button onClick={cancelEdit} className="px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">Cancel</button>
                        <button onClick={() => submitEdit(item.id)} disabled={renaming || !editingName.trim()} className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">{renaming ? 'Saving...' : 'Save'}</button>
                      </div>
                    </div>
                  )}

                  {isConfirmingDelete && (
                    <div className="flex-1 px-4 py-3">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-700 dark:text-gray-300">Delete "{item.name}"? This cannot be undone.</div>
                        <div className="flex items-center space-x-2">
                          <button onClick={cancelDelete} className="px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">Cancel</button>
                          <button onClick={() => submitDelete(item.id)} disabled={deleting} className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">{deleting ? 'Deleting...' : 'Delete'}</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {!isEditing && !isConfirmingDelete && (
                    <div className="flex items-center space-x-1 px-2">
                      <button onClick={() => startEdit(item)} className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400" title="Rename">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                      </button>
                      {items.length > 1 && (
                        <button onClick={() => requestDelete(item.id)} className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400" title="Delete">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1 1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {filteredItems.length === 0 && (
              <div className="px-4 py-6 text-sm text-gray-600 dark:text-gray-300">No {cfg.itemLabel}s match your filter.</div>
            )}
          </div>
        </div>

        
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default TokenAssignmentModal;


